import { theme } from '@prose-ui';
import { legacyTheme, styled } from '@prose-ui/legacy';

import { Typography } from 'Components/Typography';

import ConsultationCTA from 'Containers/ConsultationCTA';

import howWorksContent from 'assets/content/howWorksContent';

import useResponsiveVariant from 'utils/useResponsiveVariant';

import BlockLayout from './BlockLayout';

const Title = styled(Typography)`
  width: 70%;

  ${legacyTheme.breakpoints.up('sm')} {
    width: 100%;
  }
`;

const StepsContainer = styled.div`
  width: 100%;
  margin: ${legacyTheme.spacing.s48} auto 0 auto;
  counter-reset: step;

  ${legacyTheme.breakpoints.up('sm')} {
    margin-top: ${legacyTheme.spacing.s64};
  }

  ${legacyTheme.breakpoints.up('md')} {
    display: flex;
    justify-content: space-between;
  }
`;

const StepBlock = styled.div`
  display: grid;
  column-gap: ${legacyTheme.spacing.s40};
  justify-content: center;
  grid-template-columns: auto minmax(180px, 300px);
  margin: 0 auto;

  &:not(:last-child) {
    margin-bottom: ${legacyTheme.spacing.s64};
  }

  ${legacyTheme.breakpoints.up('sm')} {
    column-gap: ${legacyTheme.spacing.s72};
  }

  ${legacyTheme.breakpoints.up('md')} {
    display: block;
    max-width: 300px;
  }
`;

const StepImage = styled.img`
  display: block;
  margin-bottom: ${legacyTheme.spacing.s32};
  width: 75px;
  height: 90px;

  ${legacyTheme.breakpoints.up('md')} {
    height: 96px;
  }
`;

const StepTitleWrapper = styled.div`
  position: relative;
  border-bottom: 1px solid white;

  ${legacyTheme.breakpoints.up('md')} {
    height: 56px;
  }
`;

const StepTitle = styled(Typography)`
  width: 100%;
  padding-right: 20%;

  &::after {
    content: counter(step, decimal-leading-zero);
    counter-increment: step;

    position: absolute;
    right: 0;

    font-family: ${legacyTheme.typography.simplonNorm};
    font-size: 18px;
  }

  ${legacyTheme.breakpoints.up('md')} {
    position: absolute;
    bottom: 0;
  }
`;

const StepParagraph = styled(Typography)`
  margin-top: ${legacyTheme.spacing.s16};
`;

const StyledConsultationCTA = styled(ConsultationCTA)`
  margin: ${legacyTheme.spacing.s64} auto 0 auto;

  ${legacyTheme.breakpoints.up('md')} {
    margin: 0 auto;
  }
`;

type HowItWorksProps = {
  cta?: boolean;
  dataFrom?: string;
  isHaircareOnly?: boolean;
  isSkincareOnly?: boolean;
};

export const HowItWorks = ({
  cta = false,
  dataFrom = 'block-press',
  isHaircareOnly = false,
  isSkincareOnly = false,
}: HowItWorksProps) => {
  const { isMobile } = useResponsiveVariant();

  const content = isHaircareOnly
    ? howWorksContent.haircare
    : isSkincareOnly
    ? howWorksContent.skincare
    : howWorksContent.haircareAndSkincare;
  const { title, elements } = content;

  return (
    // @ts-expect-error - BlockLayout has no TS types for now
    <BlockLayout
      backgroundColor={theme.colors.primary['300']}
      contentWidth="veryWide"
      dataTestId="skincare-how-it-works"
    >
      <Title
        align={isMobile ? 'left' : 'center'}
        color="white"
        markupName="h2"
        paragraph
        variant="h1r"
      >
        {title}
      </Title>

      <StepsContainer>
        {elements.map((element) => (
          <StepBlock key={element.description}>
            <StepImage alt={element.asset.alt} src={element.asset.icon} />
            <div>
              <StepTitleWrapper>
                <StepTitle bold color="white" markupName="h3" paragraph variant="mono1">
                  {element.title}
                </StepTitle>
              </StepTitleWrapper>

              <StepParagraph color="white" paragraph variant="p2">
                {element.description}
              </StepParagraph>
            </div>
          </StepBlock>
        ))}
      </StepsContainer>

      {cta && <StyledConsultationCTA dataFrom={dataFrom} fullWidth={isMobile} variant="whiteBg" />}
    </BlockLayout>
  );
};
